/**
 * Core Base API Configuration
 * 
 * This module provides the foundational API setup for all tenants.
 * It handles:
 * - Axios instance configuration
 * - Authentication headers (EspoCRM compatible)
 * - Request/Response interceptors
 * - Error handling
 * 
 * Tenant-specific endpoints should be defined in their respective tenant modules.
 */

import {
  createApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import qs from 'qs';

/**
 * Creates a base query function with authentication and error handling.
 * This is the foundation for all API calls across tenants.
 * 
 * @param baseUrl - The API base URL (from tenant config or environment)
 * @param getToken - Function to retrieve the current auth token from state
 * @param getUsername - Function to retrieve the current username from state
 * @param onAuthError - Optional callback when authentication fails (401/403)
 */
export function createBaseQuery(
  baseUrl: string,
  getToken: (state: unknown) => string | null,
  getUsername: (state: unknown) => string | null,
  onAuthError?: (error: FetchBaseQueryError) => void
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = getToken(getState());
      const username = getUsername(getState());
      
      // Skip auth header if a custom authorization is already set (e.g., for public endpoints)
      const existingAuth = headers.get('authorization');
      if (token && username && !existingAuth) {
        headers.set('authorization', `Basic ${btoa(username + ':' + token)}`);
      }
      
      // Cache control headers
      headers.set('cache-control', 'no-cache');
      headers.set('pragma', 'no-cache');
      
      return headers;
    },
    paramsSerializer: (params) => {
      return qs.stringify(params);
    }
  });

  // Wrapper with auth error handling
  const baseQueryWithAuthCheck: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    
    if (result.error && onAuthError) {
      const status = result.error.status;
      // Check for authentication errors (401 Unauthorized, 403 Forbidden)
      if (status === 401 || status === 403) {
        onAuthError(result.error);
      }
    }
    
    return result;
  };

  return baseQueryWithAuthCheck;
}

/**
 * Creates the RTK Query API instance.
 * This is the base API that tenants will use to inject their endpoints.
 * 
 * @param baseUrl - The API base URL
 * @param getToken - Function to get auth token from Redux state
 * @param getUsername - Function to get username from Redux state
 * @param onAuthError - Optional auth error handler
 */
export function createTenantApi(
  baseUrl: string,
  getToken: (state: unknown) => string | null,
  getUsername: (state: unknown) => string | null,
  onAuthError?: (error: FetchBaseQueryError) => void
) {
  return createApi({
    reducerPath: 'tenantApi',
    baseQuery: createBaseQuery(baseUrl, getToken, getUsername, onAuthError),
    endpoints: () => ({})
  });
}

/**
 * Type exports for tenant API implementations
 */
export type { FetchArgs, FetchBaseQueryError };

